import React, { useCallback, useEffect, useReducer, useState } from "react";
import {
  CustomAutocomplete,
  CustomButton,
  CustomCard,
  CustomDialog,
  DropzoneComponent,
} from "../../../../../../../../Components";
import { useSelectedTheme, useTranslate } from "../../../../../../../../Hooks";
import { Box, Divider, Typography } from "@material-ui/core";
import { DeleteIcon, PlusIcon } from "../../../../../../../../assets/icons";
import {
  CreateScopeDocument,
  GetScopeCategoryDocuments,
  lookupItemsGetId,
  UpdateScopeDocument,
} from "../../../../../../../../Services";
import { StaticLookupsIds } from "../../../../../../../../assets/json/StaticLookupsIds";
import { GetFileTypeIcon } from "../../../../../../NewContactsView/ContactOverview/UI";
import { ScopeDocumentEnum } from "../../../../../../../../Enums";
import { useVerticalNav } from "../../../../../../../../Contexts/VerticalNavContext";

// styles
import useStyles from "./styles";

function PropertyUploadDocument({
  open,
  close,
  propertyId,
  refetchData,
  activeItem,
}) {
  const { translate } = useTranslate("NewPropertiesView");
  const {
    theme: { palette },
  } = useSelectedTheme();
  const styles = useStyles();
  const { setAlertBoxContent } = useVerticalNav();
  const reducer = useCallback((state, action) => {
    if (action.id !== "edit") return { ...state, [action.id]: action.value };
    return {
      ...action.value,
    };
  }, []);

  const [propertiesDocumentsFiles, setPropertiesDocumentsFiles] = useState([]);
  const [isOnSubmitLoading, setIsOnSubmitLoading] = useState(false);
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadedFilesTableArray, setUploadedFilesTableArray] = useState([]);
  const [categoryDocumentsData, setCategoryDocumentsData] = useState([]);
  const [filesAccepted, setFilesAccepted] = useState([]);
  const [selected, setSelected] = useReducer(reducer, {
    category: null,
  });

  const handleSubmit = async () => {
    let groupedByCategory;
    try {
      setIsOnSubmitLoading(true);
      if (activeItem) {
        groupedByCategory = [
          {
            categoryId: activeItem.categoryId,
            files: uploadedFilesTableArray.map((file) => ({
              fileId: file.fileId,
              fileName: file.fileName,
              documentLink: null,
            })),
          },
        ];
      } else {
        groupedByCategory = uploadedFilesTableArray.reduce((acc, file) => {
          const { categoryId, fileId, fileName } = file;
          const existingCategory = acc.find(
            (item) => item.categoryId === categoryId
          );

          const fileEntry = {
            fileId,
            fileName,
            documentLink: null,
          };

          if (existingCategory) {
            existingCategory.files.push(fileEntry);
          } else {
            acc.push({
              categoryId,
              files: [fileEntry],
            });
          }

          return acc;
        }, []);
      }
      const body = {
        scopeId: +propertyId,
        scopeTypeId: ScopeDocumentEnum.Property.scopeTypeId,
        categoryFiles: groupedByCategory,
      };

      const res = activeItem
        ? await UpdateScopeDocument(body)
        : await CreateScopeDocument(body);

      if (!(res && res.status && res.status !== 200)) {
        setAlertBoxContent({
          display: true,
          variant: "success",
          title: activeItem
            ? translate("Updated_SUCCESS_MESSAGE")
            : translate("SUBMISSION_SUCCESS_MESSAGE"),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
        close();
        setUploadedFiles([]);
        refetchData();
      } else {
        setAlertBoxContent({
          display: true,
          variant: "errror",
          title: translate(
            "Category-Already-has-an-image-Please-update-the-existing-image-instead"
          ),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
      }
    } catch (error) {
      console.error("An error occurred during submission:", error);
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate("SUBMISSION_FAILED_MESSAGE"),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
    } finally {
      setIsOnSubmitLoading(false);
    }
  };

  const handleAddMoreDoc = () => {
    if (!selected.category || uploadedFiles.length === 0) return;

    const newEntries = uploadedFiles.map((file) => ({
      categoryName: selected.category.lookupItemName,
      categoryId: selected.category.lookupItemId,
      fileType: file.fileType,
      fileName: file.fileName,
      fileSize: file.fileSize,
      fileId: file.fileId,
    }));

    setUploadedFilesTableArray((prev) => [...prev, ...newEntries]);

    setUploadedFiles([]);
    if (!activeItem) {
      setSelected({ id: "category", value: null });
    }
  };

  const handleFilesAccepted = (files) => {
    setFilesAccepted(files);
  };

  const handleDeleteFileFromUploadSection = (index) => {
    setUploadedFiles((prevUploadedFiles) =>
      prevUploadedFiles.filter((_, i) => i !== index)
    );
  };

  const lookupPropertiesDocumentsFiles = useCallback(async () => {
    const result = await lookupItemsGetId({
      lookupTypeId: StaticLookupsIds.UnitsDocuments, // Lookups.,
    });
    if (!(result && result.status && result.status !== 200))
      setPropertiesDocumentsFiles(result);
    else setPropertiesDocumentsFiles([]);
  }, []);

  const initalizeActiveData = () => {
    if (!activeItem || categoryDocumentsData.length === 0) return;
    const newEntries = categoryDocumentsData.map((file) => ({
      categoryName: file?.categoryName,
      categoryId: file?.categoryId,
      fileType: file?.documentName,
      fileName: file?.documentName,
      fileId: file?.documentId,
    }));

    setUploadedFilesTableArray((prev) => [...prev, ...newEntries]);
    setSelected({
      id: "category",
      value: {
        lookupItemName: categoryDocumentsData[0]?.categoryName,
        lookupItemId: categoryDocumentsData[0]?.categoryName,
      },
    });
    setUploadedFiles([]);
  };

  const getScopeCategoryDocuments = useCallback(async () => {
    const body = {
      categoryId: +activeItem.categoryId,
      scopeId: propertyId,
      pageSize: 25,
      pageIndex: 0,
    };

    const res = await GetScopeCategoryDocuments(body);

    if (!(res && res.status && res.status !== 200)) {
      setCategoryDocumentsData(res.result);
    } else {
      setCategoryDocumentsData([]);
    }
  });

  const handleFileUploadMapping = () => {
    if (uploadedFileId) {
      setUploadedFiles((prevUploadedFiles) => [
        ...prevUploadedFiles,
        filesAccepted[0],
      ]);
      setUploadedFiles((prevUploadedFiles) => {
        const updatedFiles = [...prevUploadedFiles];
        if (updatedFiles?.length > 0) {
          updatedFiles[updatedFiles.length - 1] = {
            ...updatedFiles[updatedFiles.length - 1],
            fileId: uploadedFileId,
            fileSize: updatedFiles[updatedFiles?.length - 1]?.size,
            fileName: updatedFiles[updatedFiles?.length - 1]?.path,
            fileType: updatedFiles[updatedFiles?.length - 1]?.type,
          };
        }
        return updatedFiles;
      });
    }
  };

  const handleDeleteFileFromTable = (index) => {
    setUploadedFilesTableArray((prevLinks) =>
      prevLinks.filter((_, i) => i !== index)
    );
  };

  useEffect(() => {
    lookupPropertiesDocumentsFiles();
  }, []);

  useEffect(() => {
    handleFileUploadMapping();
  }, [uploadedFileId]);

  useEffect(() => {
    if (activeItem) {
      getScopeCategoryDocuments();
    }
  }, [activeItem]);

  useEffect(() => {
    initalizeActiveData();
  }, [categoryDocumentsData]);

  return (
    <CustomDialog
      onConfirm={handleSubmit}
      isDisabled={
        activeItem
          ? false
          : selected.category !== null ||
            uploadedFiles.length > 0 ||
            uploadedFilesTableArray.length === 0 ||
            isOnSubmitLoading
      }
      open={open}
      onClose={() => {
        close();
      }}
      width={"688px"}
      title={translate("upload_document")}
      subtitle={translate(
        "Add-new-documents-or-files-related-to-this-Property-for-secure-storage-and-easy-access"
      )}
      confirmText={translate("Upload")}
      content={
        <Box className={styles.containerContentDialog}>
          <Box className={styles.containerFiledItem}>
            <Typography className={styles.titleFiled}>
              {translate("Attachment")}
            </Typography>
            <Box className={styles.containerAttatcmentFiled}>
              <CustomAutocomplete
                placeholder={translate("Select-category")}
                options={propertiesDocumentsFiles}
                value={selected?.category || null}
                onChange={(e, value) => {
                  setSelected({ id: "category", value: value });
                }}
                getOptionLabel={(option) => option?.lookupItemName || ""}
                getOptionValue={(option) => option?.lookupItemId || ""}
                renderOption={(option) => option?.lookupItemName || ""}
                isDisabled={activeItem}
              />
              <DropzoneComponent
                accept="image/*,
                                 video/*,
                                 application/pdf,
                                 application/msword,
                                 application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                                 application/vnd.ms-excel,
                                 application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
                                 text/plain,
                                 application/vnd.adobe.figma,
                                 application/illustrator,
                                 application/photoshop,
                                 application/vnd.adobe.indesign,
                                 application/aftereffects,
                                 audio/mp3,
                                 audio/wav,
                                 video/mp4,
                                 video/mpeg,
                                 video/avi,
                                 video/mkv,
                                 text/html,
                                 text/css,
                                 application/rss+xml,
                                 application/sql,
                                 application/javascript,
                                 application/json,
                                 text/x-java-source,
                                 application/xml,
                                 application/x-msdownload,
                                 application/x-dmg,
                                 application/zip,
                                 application/x-rar-compressed"
                title={translate("SVG-PNG-JPG-or-GIF-max-800x400px")}
                onFilesAccepted={handleFilesAccepted}
                setUploadedFileId={setUploadedFileId}
              />
              {uploadedFiles?.length > 0 && (
                <Box className={styles.uploadedFilesContainer}>
                  {uploadedFiles
                    .filter((file) => file?.fileType)
                    .map((uploadFile, index) => (
                      <CustomCard
                        boxShadow="xs"
                        borderRadius="xl"
                        borderColor="secondary"
                        classes={styles.uploadedFileCard}
                        key={index}
                      >
                        {GetFileTypeIcon(uploadFile?.fileType)}
                        <Box className={styles.uploadedFileDetailsContainer}>
                          <Box className={styles.fileHeaderContainer}>
                            <Typography className={styles.fileNameText}>
                              {uploadFile?.fileName}
                            </Typography>
                            <DeleteIcon
                              width="20"
                              height="20"
                              className={styles.DeleteIcon}
                              style={{ cursor: "pointer" }}
                              fill={palette.button.secondary_fg}
                              onClick={() =>
                                handleDeleteFileFromUploadSection(index)
                              }
                            />
                          </Box>
                          <Typography className={styles.fileSizeText}>
                            {uploadFile.fileSize
                              ? `${uploadFile?.fileSize} bytes`
                              : "Unknown Size"}
                          </Typography>
                        </Box>
                      </CustomCard>
                    ))}
                </Box>
              )}
              <Box>
                <CustomButton
                  size="sm"
                  variant="text"
                  color="tertiaryGray"
                  style={{ display: "flex", gap: "12px" }}
                  startIcon={
                    <PlusIcon
                      width="20"
                      height="20"
                      fill={palette.button.tertiaryGray_fg}
                    />
                  }
                  onClick={() => {
                    handleAddMoreDoc();
                  }}
                  disabled={!selected.category || uploadedFiles.length === 0}
                >
                  {translate("Add-another")}
                </CustomButton>
              </Box>
            </Box>
          </Box>
          {uploadedFilesTableArray?.length > 0 && (
            <CustomCard
              boxShadow="xs"
              borderRadius="xl"
              borderColor="secondary"
              classes={styles.MoreUploadContainer}
            >
              <Box className={styles.HeaderTableSection}>
                <Typography className={styles.TitleItemTable}>
                  {translate("Category")}
                </Typography>
                <Typography className={styles.TitleItemTable}>
                  {translate("Attachment-Name")}
                </Typography>
                <Typography className={styles.TitleItemTable}>
                  {translate("Action")}
                </Typography>
              </Box>
              <Divider />
              <Box className={styles.BodyTableSection}>
                {uploadedFilesTableArray?.map((file, index) => (
                  <Box key={index} className={styles.rowMoreUpload}>
                    <Typography className={styles.categoryName}>
                      {file?.categoryName}
                    </Typography>
                    <Box className={styles.containerFile_Name}>
                      {GetFileTypeIcon(file?.fileType)}
                      <Typography className={styles.fileName}>
                        {file?.fileName}
                      </Typography>
                    </Box>
                    <Box className={styles.containerDelete_Icon}>
                      <DeleteIcon
                        width="20"
                        height="20"
                        fill={palette.button.tertiaryGray_fg}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleDeleteFileFromTable(index)}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </CustomCard>
          )}
        </Box>
      }
    />
  );
}

export default PropertyUploadDocument;
